import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { trpc } from '@/api/trpc';
import { showToast } from '@/components/Layout';
import { buildIdeaLabRulesEditorPath, nextNudgeVariantKey, type IdeaLabVariantSummary } from './ideaLabUtils';

/**
 * "Make a variation": copies a variant into a new editable draft that keeps it as its parent, then
 * opens the draft's rules. Keys keep the stable `<key>-nudge-N` form existing variants already use.
 */
export const useMakeVariation = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const mutation = useMutation({
        ...trpc.ideaLab.createVariant.mutationOptions(),
        onError: (error) => showToast(error.message, 'error'),
        onSuccess: async (created) => {
            showToast(`Created draft "${created.label}"; opening its rules`, 'success');
            await queryClient.invalidateQueries(trpc.ideaLab.getStudy.pathFilter());
            void navigate(buildIdeaLabRulesEditorPath(created.id));
        }
    });

    const makeVariation = (
        source: Pick<IdeaLabVariantSummary, 'id' | 'studyId' | 'label' | 'variantKey' | 'primaryDuration'>,
        studyVariantKeys: readonly string[]
    ) =>
        mutation.mutate({
            studyId: source.studyId,
            variantKey: nextNudgeVariantKey(studyVariantKeys, source.variantKey),
            label: `${source.label} (variation)`,
            description: `Variation of ${source.variantKey}.`,
            primaryDuration: source.primaryDuration,
            source: { kind: 'variant', variantId: source.id }
        });

    return { makeVariation, isPending: mutation.isPending };
};
